import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { SavingArenaApiService } from '../api/saving-arena-api.service';

/**
 * Submit state for one newsletter form: the field, the honeypot, and the in-flight/done/error flags.
 *
 * Deliberately **not** `providedIn: 'root'` — every form needs its own instance, so provide it on
 * the component that renders the form (`providers: [NewsletterSignup]`). It exists because the same
 * signup lives in several visually different places (the BIG SALE CTA, the footer, the blog CTAs):
 * they share this logic and keep their own markup.
 *
 * Emails land in the admin panel under Newsletter Subscribers.
 */
@Injectable()
export class NewsletterSignup {
  private readonly api = inject(SavingArenaApiService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly form = inject(FormBuilder).group({
    email: ['', [Validators.required, Validators.email]],
    companyWebsite: [''], // honeypot — bots fill it, real users never see it
  });

  readonly submitting = signal(false);
  readonly subscribed = signal(false);
  readonly error = signal('');

  /** True once the field has been touched and is invalid — for the inline field message. */
  showEmailError(): boolean {
    const control = this.form.controls.email;
    return control.invalid && control.touched;
  }

  /** `source` tags which form this is; the backend allows a fixed set (see NEWSLETTER_SOURCES). */
  submit(source: string): void {
    if (String(this.form.controls.companyWebsite.value ?? '').length > 0) {
      this.subscribed.set(true);
      return; // honeypot tripped — report success, send nothing
    }

    if (this.form.invalid || this.submitting()) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.set(true);
    this.error.set('');

    this.api
      .subscribeToNewsletter({
        email: String(this.form.controls.email.value ?? '').trim(),
        source,
        sourcePath: this.router.url,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // An address that is already on the list resolves here too, so a repeat signup reads as
        // success rather than as an error the visitor can't act on.
        next: () => {
          this.submitting.set(false);
          this.subscribed.set(true);
          this.form.reset({ email: '', companyWebsite: '' });
        },
        error: (err: unknown) => {
          this.submitting.set(false);
          const status = (err as { status?: number }).status;
          this.error.set(
            status === 422
              ? 'Enter a valid email address.'
              : 'Could not subscribe right now. Please try again.',
          );
        },
      });
  }
}
